import * as d3 from 'd3-ease';
import * as turf from '@turf/turf';
import mapboxgl from "mapbox-gl";

import { getWindowPercentageInPx } from "../utils";
import { findFirstLevelIdOfStage } from '../finder';
import { createFeatureCollection, getLevelData, projectLevelSource } from "./mapboxDataSource";

export function updateMapLevelImage(map, stageIds, levelIds, projectData) {
    let levelId;
    if (levelIds.length > 0) {
        levelId = levelIds[levelIds.length - 1];
    } else if (stageIds.length > 0) {
        const lastStageId = stageIds[stageIds.length - 1];
        levelId = findFirstLevelIdOfStage(projectData.levels, lastStageId);
    } else {
        levelId = Object.keys(projectData.levels)[0];
    }
    if (levelId) {
        const levelData = getLevelData(projectData, projectData.levels[levelId]);
        const projectLevelImageSource = projectLevelSource(levelData);
        const mapImageSource = map.getSource('projectLevel');
        if (mapImageSource.url !== projectLevelImageSource.url) {
            mapImageSource.updateImage(projectLevelImageSource);
        }
    }
}

export function updateFeaturesOnMapAndFitCamera(map, featuresGroups) {
    let allFeatures = [];
    for (const featureGroupId in featuresGroups) {
        allFeatures = allFeatures.concat(featuresGroups[featureGroupId]);
    }
    const featureCollection = createFeatureCollection(allFeatures);
    map.getSource('cards').setData(featureCollection.data);
    if (featureCollection.data.features.length) {
        fitCameraOnMapFeatures(map, featureCollection);
    }
}

// Track camera fitting state per map instance to avoid issues when switching projects
const mapCameraState = new WeakMap();

function getCameraState(map) {
    if (!mapCameraState.has(map)) {
        mapCameraState.set(map, { fitted: false, retryPending: false });
    }
    return mapCameraState.get(map);
}

function isValidNumber(value) {
    return typeof value === 'number' && isFinite(value) && !isNaN(value);
}

function isValidBbox(bbox) {
    if (!bbox || bbox.length !== 4) return false;
    return bbox.every(value => isValidNumber(value));
}

function isValidCoordinate(coord) {
    return Array.isArray(coord) && coord.length >= 2 && 
           isValidNumber(coord[0]) && isValidNumber(coord[1]);
}

function hasValidCoordinates(features) {
    if (!features || features.length === 0) return false;
    return features.some(feature => {
        if (!feature.geometry || !feature.geometry.coordinates) return false;
        const coords = feature.geometry.coordinates;
        const type = feature.geometry.type;
        
        if (type === 'Point') {
            return isValidCoordinate(coords);
        } else if (type === 'LineString') {
            return Array.isArray(coords) && coords.length > 0 && coords.some(isValidCoordinate);
        } else if (type === 'Polygon') {
            return Array.isArray(coords) && coords.length > 0 && 
                   Array.isArray(coords[0]) && coords[0].some(isValidCoordinate);
        }
        return false;
    });
}

function getSafePadding(percentage, defaultValue = 50) {
    const value = getWindowPercentageInPx(percentage);
    return isValidNumber(value) && value > 0 ? value : defaultValue;
}

function hasValidMapDimensions(map) {
    try {
        const container = map.getContainer();
        if (!container) return false;
        const width = container.clientWidth || container.offsetWidth;
        const height = container.clientHeight || container.offsetHeight;
        return isValidNumber(width) && isValidNumber(height) && width > 0 && height > 0;
    } catch (e) {
        return false;
    }
}

function performFitBounds(map, bounds, paddingInPx, paddingTopInPx) {
    try {
        map.fitBounds(bounds, {
            padding: {
                top: paddingTopInPx,
                bottom: paddingInPx,
                left: paddingInPx,
                right: paddingInPx,
            },
            bearing: 0,
            pitch: 60,
            easing: d3.easeCubic
        });
        return true;
    } catch (error) {
        console.warn('fitCameraOnMapFeatures: Error in fitBounds', error);
        return false;
    }
}

export function fitCameraOnMapFeatures(map, featureCollection) {
    const state = getCameraState(map);
    
    // Already fitted for this map instance
    if (state.fitted) return;
    
    try {
        // Check if map has valid dimensions
        if (!hasValidMapDimensions(map)) {
            // Only register one retry listener at a time
            if (!state.retryPending) {
                state.retryPending = true;
                console.warn('fitCameraOnMapFeatures: Map container has invalid dimensions, will retry when idle');
                map.once('idle', () => {
                    state.retryPending = false;
                    // Re-call with fresh state check
                    if (!state.fitted) {
                        fitCameraOnMapFeatures(map, featureCollection);
                    }
                });
            }
            return;
        }
        
        // Validate features
        if (!featureCollection?.data?.features || 
            !hasValidCoordinates(featureCollection.data.features)) {
            console.warn('fitCameraOnMapFeatures: No valid features to fit bounds');
            return;
        }
        
        const featuresBbox = turf.bbox(featureCollection.data);
        
        if (!isValidBbox(featuresBbox)) {
            console.warn('fitCameraOnMapFeatures: Invalid bounding box calculated', featuresBbox);
            return;
        }
        
        const bboxPolygon = turf.bboxPolygon(featuresBbox);
        const coordinates = bboxPolygon.geometry.coordinates[0];
        
        if (!coordinates || coordinates.length === 0 || !coordinates.every(isValidCoordinate)) {
            console.warn('fitCameraOnMapFeatures: Invalid coordinates from bbox polygon', coordinates);
            return;
        }
        
        const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);
        for (const coord of coordinates) {
            bounds.extend(coord);
        }
        
        // Validate bounds
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        if (!sw || !ne || 
            !isValidNumber(sw.lng) || !isValidNumber(sw.lat) ||
            !isValidNumber(ne.lng) || !isValidNumber(ne.lat)) {
            console.warn('fitCameraOnMapFeatures: Invalid bounds created', { sw, ne });
            return;
        }
        
        // Calculate safe padding
        const container = map.getContainer();
        const containerWidth = container.clientWidth || 0;
        const containerHeight = container.clientHeight || 0;
        
        let paddingInPx = getSafePadding(0.3, 50);
        let paddingTopInPx = getSafePadding(0.5, 80);
        
        const minDimension = 100;
        if (containerWidth - (paddingInPx * 2) < minDimension || 
            containerHeight - paddingTopInPx - paddingInPx < minDimension) {
            // Use smaller padding for small viewports
            paddingInPx = Math.max(10, Math.min(paddingInPx, containerWidth / 4, containerHeight / 4));
            paddingTopInPx = paddingInPx;
        }
        
        if (performFitBounds(map, bounds, paddingInPx, paddingTopInPx)) {
            state.fitted = true;
        }
        // If fitBounds fails, don't mark as fitted - allow retry on next call
    } catch (error) {
        console.warn('fitCameraOnMapFeatures: Error fitting bounds', error);
        // Don't mark as fitted on error - allow retry
    }
}

export function removeFeaturesHighlight(map) {
    const features = map.querySourceFeatures('cards');
    features.forEach(feature => {
        map.setFeatureState(
            { source: 'cards', id: feature.id },
            { highlight: false }
        );
    })
}